import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowDown, ArrowUp, Play, FileText, Pencil, Archive as ArchiveIcon,
  ArchiveRestore, Users, Search, Plus, Tag as TagIcon,
} from "lucide-react";
import { toast } from "sonner";
import JeaInput from "@/components/jea/JeaInput";
import JeaButton from "@/components/jea/JeaButton";
import EmptyState from "@/components/jea/EmptyState";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { centsToUSD, shortDate } from "@/lib/dates";
import { TIERS } from "@/lib/pricing";
import { cn } from "@/lib/utils";
import { TierBadge } from "@/components/dashboard/DebtorRowCard";
import TagPill from "@/components/tags/TagPill";
import TagAssignPopover from "@/components/tags/TagAssignPopover";
import { useTags } from "@/context/TagsContext";
import { hexToRgba, resolveTags } from "@/lib/tags";
import { usePreviewGate } from "@/hooks/usePreviewGate";
import { useRolePermissions } from "@/hooks/useRolePermissions";

const STATUSES = ["active", "attention_required", "archived"];
const PAGE_SIZE = 25;

const StatusPill = ({ status }) => (
  <span
    className={cn(
      "inline-flex px-2 py-1 rounded-full text-[11px] font-bold tracking-[0.08em]",
      status === "active" && "bg-success/15 text-success border border-success/40",
      status === "attention_required" && "bg-warning/15 text-warning border border-warning/40",
      status === "archived" && "bg-danger/15 text-danger border border-danger/40"
    )}
  >
    {status.replace("_", " ").toUpperCase()}
  </span>
);

const HeaderCell = ({ label, sortKey, sortState, onSort, testid }) => {
  const active = sortState.key === sortKey;
  const Arrow = sortState.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      onClick={() => onSort(sortKey)}
      className="p-4 text-left cursor-pointer select-none group"
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 jea-caption text-text-secondary group-hover:text-white transition-colors">
        {label}
        {active && <Arrow size={12} className="text-accent-pink" data-testid={`${testid}-arrow`} />}
      </div>
    </th>
  );
};

const ProDebtorTable = ({ debtors, onArchived, onRunQuery, onViewReports, onRestored }) => {
  const nav = useNavigate();
  const { guardAction } = usePreviewGate();
  const { canMutateDebtors, canRunQueries } = useRolePermissions();
  const handleAddDebtor = guardAction(() => nav("/debtors/new"), { feature: "adding judgments" });
  const { tags, lookup, isPro } = useTags();
  const [search, setSearch] = useState("");
  const [statusFilters, setStatusFilters] = useState(new Set(["active"]));
  const [tierFilters, setTierFilters] = useState(new Set());
  const [tagFilters, setTagFilters] = useState(new Set());
  const [sort, setSort] = useState({ key: "next_query_at", dir: "asc" });
  const [page, setPage] = useState(1);
  const [popover, setPopover] = useState({ open: false, debtorId: null, tagIds: [], rect: null });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filteredList = debtors.filter((d) => {
      if (statusFilters.size && !statusFilters.has(d.status)) return false;
      if (tierFilters.size && !tierFilters.has(d.tier)) return false;
      if (isPro && tagFilters.size) {
        const dtags = new Set(d.tag_ids || []);
        let any = false;
        for (const t of tagFilters) { if (dtags.has(t)) { any = true; break; } }
        if (!any) return false;
      }
      if (q) {
        const hay = [
          d.full_name || "",
          d.judgment?.case_number || "",
          d.judgment?.court_name || "",
        ].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    const dir = sort.dir === "asc" ? 1 : -1;
    filteredList.sort((a, b) => {
      let av;
      let bv;
      switch (sort.key) {
        case "full_name":
          av = a.full_name || "";
          bv = b.full_name || "";
          return av.localeCompare(bv) * dir;
        case "amount_cents":
          av = a.judgment?.amount_cents || 0;
          bv = b.judgment?.amount_cents || 0;
          return (av - bv) * dir;
        case "last_query_at":
          av = a.last_query_at || "";
          bv = b.last_query_at || "";
          return av.localeCompare(bv) * dir;
        case "next_query_at":
        default:
          av = a.next_query_at || "";
          bv = b.next_query_at || "";
          return av.localeCompare(bv) * dir;
      }
    });
    return filteredList;
  }, [debtors, search, statusFilters, tierFilters, tagFilters, sort, isPro]);

  useEffect(() => { setPage(1); }, [search, statusFilters, tierFilters, tagFilters]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleSet = (setState, current, key) => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setState(next);
  };

  const onSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  };

  const archive = async (id, name) => {
    try {
      await apiClient.delete(`/debtors/${id}`);
      toast.success(`Archived ${name}.`);
      onArchived && onArchived();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const restore = async (id, name) => {
    try {
      await apiClient.patch(`/debtors/${id}`, { status: "active" });
      toast.success(`Restored ${name}.`);
      onRestored ? onRestored() : (onArchived && onArchived());
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const clearFilters = () => {
    setStatusFilters(new Set(["active"]));
    setTierFilters(new Set());
    setTagFilters(new Set());
    setSearch("");
  };

  const anyExtraFilter = tierFilters.size > 0 || tagFilters.size > 0
    || !(statusFilters.size === 1 && statusFilters.has("active"));

  const openTagPopover = (d, evt) => {
    const rect = evt.currentTarget.getBoundingClientRect();
    setPopover({ open: true, debtorId: d.id, tagIds: d.tag_ids || [], rect });
  };
  const closeTagPopover = () => setPopover((p) => ({ ...p, open: false }));

  return (
    <div className="bg-bg-secondary border border-border-default rounded-[12px] overflow-hidden" data-testid="pro-debtor-table-container">
      {/* Toolbar */}
      <div className="flex items-center gap-3 p-4 flex-wrap border-b border-border-default">
        <JeaInput
          leftIcon={<Search size={16} />}
          placeholder="Search judgments by name, case number, court..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="!bg-bg-primary"
          data-testid="pro-table-search"
        />
        <div className="flex gap-1.5 items-center" data-testid="pro-table-status-filters">
          {STATUSES.map((s) => {
            const active = statusFilters.has(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleSet(setStatusFilters, statusFilters, s)}
                className={cn(
                  "h-8 px-3 rounded-full text-[11px] font-bold tracking-[0.08em] uppercase border transition-all",
                  active
                    ? "bg-accent-pink text-[#091424] border-transparent"
                    : "bg-bg-primary text-text-secondary border-border-default hover:border-accent-pink/60"
                )}
                data-testid={`filter-status-${s}`}
              >
                {s.replace("_", " ")}
              </button>
            );
          })}
        </div>
        <div className="flex gap-1.5 items-center" data-testid="pro-table-tier-filters">
          {TIERS.map((t) => {
            const active = tierFilters.has(t.key);
            const activeStyle = active
              ? { background: t.color, color: "#091424", borderColor: "transparent" }
              : undefined;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => toggleSet(setTierFilters, tierFilters, t.key)}
                className={cn(
                  "h-8 px-3 rounded-full text-[11px] font-bold tracking-[0.08em] border transition-all",
                  active
                    ? "border-transparent"
                    : "bg-bg-primary text-text-secondary border-border-default hover:border-accent-pink/60"
                )}
                style={activeStyle}
                data-testid={`filter-tier-${t.key}`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        {isPro && tags.length > 0 && (
          <div className="flex gap-1.5 items-center flex-wrap" data-testid="pro-table-tag-filters">
            {tags.map((t) => {
              const active = tagFilters.has(t.id);
              const style = active
                ? { background: hexToRgba(t.color, 0.15), color: t.color, borderColor: t.color }
                : undefined;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggleSet(setTagFilters, tagFilters, t.id)}
                  className={cn(
                    "h-8 pl-2 pr-3 rounded-full text-[11px] font-semibold border transition-all inline-flex items-center gap-1.5",
                    active
                      ? "font-bold"
                      : "bg-bg-primary text-text-secondary border-border-default hover:border-accent-pink/60"
                  )}
                  style={style}
                  data-testid={`filter-tag-${t.slug}`}
                >
                  <span className="w-2 h-2 rounded-full" style={{ background: t.color }} />
                  {t.name}
                </button>
              );
            })}
          </div>
        )}
        {anyExtraFilter && (
          <button
            type="button"
            onClick={clearFilters}
            className="h-8 px-3 rounded-full text-[11px] font-semibold border border-border-default text-text-secondary hover:text-white hover:border-white/40 transition-colors"
            data-testid="pro-table-clear-filters"
          >
            Clear all filters
          </button>
        )}
      </div>

      {debtors.length === 0 ? (
        <div className="py-14">
          <EmptyState
            icon={Users}
            title="No judgments yet"
            description={canMutateDebtors ? "Add your first judgment to start tracking enforcement." : "No judgments are visible for your role."}
            testid="pro-table-empty"
            footer={
              canMutateDebtors ? (
                <JeaButton onClick={handleAddDebtor} className="mt-2 gap-2" data-testid="pro-table-empty-add">
                  <Plus size={16} /> Add New Judgment
                </JeaButton>
              ) : null
            }
          />
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="pro-debtor-table">
              <thead>
                <tr className="bg-bg-tertiary">
                  <HeaderCell label="Debtor Name" sortKey="full_name" sortState={sort} onSort={onSort} testid="sort-name" />
                  <th className="p-4 text-left jea-caption text-text-secondary">Tier</th>
                  {isPro && <th className="p-4 text-left jea-caption text-text-secondary">Tags</th>}
                  <HeaderCell label="Next Query" sortKey="next_query_at" sortState={sort} onSort={onSort} testid="sort-next-query" />
                  <HeaderCell label="Last Query" sortKey="last_query_at" sortState={sort} onSort={onSort} testid="sort-last-query" />
                  <HeaderCell label="Judgment Amount" sortKey="amount_cents" sortState={sort} onSort={onSort} testid="sort-amount" />
                  <th className="p-4 text-left jea-caption text-text-secondary">Status</th>
                  <th className="p-4 text-left jea-caption text-text-secondary">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((d) => (
                  <tr
                    key={d.id}
                    className="border-b border-border-default hover:bg-bg-tertiary hover:border-l-2 hover:border-l-accent-pink transition-colors group"
                    data-testid={`pro-row-${d.id}`}
                  >
                    <td className="p-4">
                      <button
                        type="button"
                        onClick={() => nav(`/debtors/${d.id}`)}
                        className="text-white font-semibold hover:text-accent-pink transition-colors text-[14px] text-left"
                        data-testid={`debtor-name-${d.id}`}
                      >
                        {d.full_name}
                      </button>
                      <div className="text-[12px] jea-caseno">{d.judgment?.case_number || "—"}</div>
                    </td>
                    <td className="p-4"><TierBadge tier={d.tier} testid={`debtor-tier-${d.id}`} /></td>
                    {isPro && (
                      <td className="p-4">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); openTagPopover(d, e); }}
                          className="inline-flex flex-wrap gap-1 items-center max-w-[240px] text-left hover:opacity-80 transition-opacity"
                          data-testid={`row-tags-cell-${d.id}`}
                        >
                          {(d.tag_ids || []).length === 0 ? (
                            <span className="inline-flex items-center gap-1 text-text-muted text-[12px] border border-dashed border-border-default rounded-[4px] px-2 py-[2px]">
                              <TagIcon size={11} /> Add
                            </span>
                          ) : (
                            <>
                              {resolveTags(d.tag_ids, lookup).slice(0, 3).map((t) => (
                                <TagPill key={t.id} tag={t} />
                              ))}
                              {(d.tag_ids || []).length > 3 && (
                                <span className="text-[11px] font-semibold text-text-secondary">+{d.tag_ids.length - 3}</span>
                              )}
                            </>
                          )}
                        </button>
                      </td>
                    )}
                    <td className="p-4 jea-date text-[13px]">{shortDate(d.next_query_at)}</td>
                    <td className="p-4 jea-date text-[13px]">{shortDate(d.last_query_at)}</td>
                    <td className="p-4 jea-mono text-accent-pink font-semibold text-[13px]" data-testid={`debtor-amount-${d.id}`}>
                      {centsToUSD(d.judgment?.amount_cents)}
                    </td>
                    <td className="p-4"><StatusPill status={d.status} /></td>
                    <td className="p-4">
                      <div className="flex items-center gap-1">
                        {canRunQueries && (
                          <IconAction Icon={Play} label="Run Query" onClick={() => onRunQuery ? onRunQuery(d) : nav(`/debtors/${d.id}`)} testid={`row-run-${d.id}`} />
                        )}
                        <IconAction Icon={FileText} label="View Report" onClick={() => onViewReports ? onViewReports(d) : nav(`/debtors/${d.id}?tab=reports`)} testid={`row-report-${d.id}`} />
                        {canMutateDebtors && (
                          <IconAction Icon={Pencil} label="Edit Profile" onClick={() => nav(`/debtors/${d.id}?edit=profile`)} testid={`row-edit-${d.id}`} />
                        )}
                        {canMutateDebtors && (d.status === "archived" ? (
                          <IconAction Icon={ArchiveRestore} label="Restore Judgment" onClick={() => restore(d.id, d.full_name)} testid={`row-restore-${d.id}`} accent />
                        ) : (
                          <IconAction Icon={ArchiveIcon} label="Archive" onClick={() => archive(d.id, d.full_name)} testid={`row-archive-${d.id}`} danger />
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-end gap-2 p-3 border-t border-border-default text-[13px] text-text-secondary" data-testid="pro-pagination">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-2 hover:text-accent-pink transition-colors disabled:opacity-40 disabled:pointer-events-none"
              data-testid="pagination-prev"
            >
              ‹
            </button>
            <span data-testid="pagination-label">
              <span className="text-white">{page}</span> of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-2 hover:text-accent-pink transition-colors disabled:opacity-40 disabled:pointer-events-none"
              data-testid="pagination-next"
            >
              ›
            </button>
          </div>
        </>
      )}

      <TagAssignPopover
        open={popover.open}
        debtorId={popover.debtorId}
        initialTagIds={popover.tagIds}
        anchorRect={popover.rect}
        onClose={closeTagPopover}
        onSaved={() => { closeTagPopover(); onArchived && onArchived(); }}
      />
    </div>
  );
};

const IconAction = ({ Icon, label, onClick, testid, danger = false, accent = false }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    className={cn(
      "p-2 rounded text-text-secondary hover:bg-bg-primary transition-colors",
      danger ? "hover:text-danger" :
      accent ? "hover:text-success" :
      "hover:text-accent-pink"
    )}
    data-testid={testid}
  >
    <Icon size={16} />
  </button>
);

export default ProDebtorTable;
